import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createCustomerPortalRequest,
  getCustomerPortalRequestOptions,
  getCustomerPortalRequest,
  getCustomerPortalRequests,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

const DEFAULT_LIMIT = 25;

export default function PortalRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [requestOptionsLoading, setRequestOptionsLoading] = useState(true);
  const [requestOptions, setRequestOptions] = useState({
    locations: [],
    installations: [],
    assets: [],
  });
  const [form, setForm] = useState({
    requestType: "service",
    subject: "",
    message: "",
    prioriteit: "normaal",
    quoteRequestMeta: {
      desiredExecutionDate: "",
      budgetIndication: "",
      contactPersonName: "",
      contactPersonPhone: "",
      contactPersonEmail: "",
      locationId: "",
      installationId: "",
      assetId: "",
    },
  });
  const limit = DEFAULT_LIMIT;
  const requestType = String(form.requestType || "").trim().toLowerCase();
  const showContextSelectors = [
    "service",
    "storing",
    "onderhoud",
    "inspectie",
    "offerte_aanvraag",
  ].includes(requestType);
  const selectedLocationId = String(form.quoteRequestMeta?.locationId || "").trim();
  const selectedInstallationId = String(form.quoteRequestMeta?.installationId || "").trim();

  const filteredInstallations = useMemo(() => {
    const rows = Array.isArray(requestOptions?.installations) ? requestOptions.installations : [];
    if (!selectedLocationId) return rows;
    return rows.filter((row) => String(row?.locationId || "").trim() === selectedLocationId);
  }, [requestOptions, selectedLocationId]);

  const filteredAssets = useMemo(() => {
    const rows = Array.isArray(requestOptions?.assets) ? requestOptions.assets : [];
    if (selectedInstallationId) {
      return rows.filter(
        (row) => String(row?.installationId || "").trim() === selectedInstallationId
      );
    }
    if (selectedLocationId) {
      return rows.filter((row) => String(row?.locationId || "").trim() === selectedLocationId);
    }
    return rows;
  }, [requestOptions, selectedInstallationId, selectedLocationId]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await getCustomerPortalRequests({
          page,
          limit,
          q: String(query || "").trim() || undefined,
          status: statusFilter || undefined,
          requestType: typeFilter || undefined,
        });
        if (!active) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total || 0));
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Aanvragen laden mislukt"));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [limit, page, query, refreshKey, statusFilter, typeFilter]);

  useEffect(() => {
    let active = true;
    async function loadRequestOptions() {
      try {
        setRequestOptionsLoading(true);
        const res = await getCustomerPortalRequestOptions();
        if (!active) return;
        setRequestOptions({
          locations: Array.isArray(res?.locations) ? res.locations : [],
          installations: Array.isArray(res?.installations) ? res.installations : [],
          assets: Array.isArray(res?.assets) ? res.assets : [],
        });
      } catch {
        if (!active) return;
        setRequestOptions({ locations: [], installations: [], assets: [] });
      } finally {
        if (active) setRequestOptionsLoading(false);
      }
    }

    loadRequestOptions();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    async function loadDetail() {
      if (!selectedId) {
        setDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        const res = await getCustomerPortalRequest(selectedId);
        if (!active) return;
        setDetail(res || null);
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Aanvraagdetail laden mislukt"));
      } finally {
        if (active) setDetailLoading(false);
      }
    }
    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedId]);

  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    return {
      page: safePage,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [limit, page, total]);

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  useEffect(() => {
    if (!selectedLocationId) return;
    if (filteredInstallations.some((row) => String(row?.id || "") === selectedInstallationId)) return;
    setForm((current) => ({
      ...current,
      quoteRequestMeta: {
        ...current.quoteRequestMeta,
        installationId: "",
        assetId: "",
      },
    }));
  }, [filteredInstallations, selectedInstallationId, selectedLocationId]);

  useEffect(() => {
    const selectedAssetId = String(form.quoteRequestMeta?.assetId || "").trim();
    if (!selectedAssetId) return;
    if (filteredAssets.some((row) => String(row?.id || "") === selectedAssetId)) return;
    setForm((current) => ({
      ...current,
      quoteRequestMeta: {
        ...current.quoteRequestMeta,
        assetId: "",
      },
    }));
  }, [filteredAssets, form.quoteRequestMeta?.assetId]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const selectedLocationId = String(form.quoteRequestMeta?.locationId || "").trim() || undefined;
      const selectedInstallationId =
        String(form.quoteRequestMeta?.installationId || "").trim() || undefined;
      const selectedAssetId = String(form.quoteRequestMeta?.assetId || "").trim() || undefined;
      const quoteRequestMetaPayload =
        String(form.requestType || "") === "offerte_aanvraag"
          ? {
              desiredExecutionDate:
                String(form.quoteRequestMeta?.desiredExecutionDate || "").trim() || undefined,
              budgetIndication:
                String(form.quoteRequestMeta?.budgetIndication || "").trim() || undefined,
              contactPersonName:
                String(form.quoteRequestMeta?.contactPersonName || "").trim() || undefined,
              contactPersonPhone:
                String(form.quoteRequestMeta?.contactPersonPhone || "").trim() || undefined,
              contactPersonEmail:
                String(form.quoteRequestMeta?.contactPersonEmail || "").trim() || undefined,
              locationId: selectedLocationId,
              installationId: selectedInstallationId,
              assetId: selectedAssetId,
            }
          : {
              locationId: selectedLocationId,
              installationId: selectedInstallationId,
              assetId: selectedAssetId,
            };
      const quoteMetaHasValue = Object.values(quoteRequestMetaPayload).some(Boolean);

      await createCustomerPortalRequest({
        subject: String(form.subject || "").trim(),
        message: String(form.message || "").trim(),
        requestType: String(form.requestType || "service"),
        prioriteit: String(form.prioriteit || "normaal"),
        quoteRequestMeta:
          String(form.requestType || "") === "offerte_aanvraag" ||
          (showContextSelectors && quoteMetaHasValue)
            ? quoteRequestMetaPayload
            : undefined,
      });
      setForm({
        requestType: "service",
        subject: "",
        message: "",
        prioriteit: "normaal",
        quoteRequestMeta: {
          desiredExecutionDate: "",
          budgetIndication: "",
          contactPersonName: "",
          contactPersonPhone: "",
          contactPersonEmail: "",
          locationId: "",
          installationId: "",
          assetId: "",
        },
      });
      setSuccess(
        String(form.requestType || "") === "offerte_aanvraag"
          ? "Uw offerte-aanvraag is ontvangen. Wij nemen deze in behandeling."
          : "Uw aanvraag is ontvangen. Wij nemen deze in behandeling."
      );
      setPage(1);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(toPortalErrorMessage(err, "Aanvraag indienen mislukt"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Aanvragen</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">Aanvragen</h1>
          <p className="text-sm text-slate-600">Dien een nieuwe aanvraag in en volg de status.</p>
        </header>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Nieuwe aanvraag</h2>
          <p className="mt-1 text-xs text-slate-500">
            Kies eerst of je een service/storingsmelding wilt doen of een offerte wilt aanvragen.
          </p>
          <form className="mt-3 grid gap-3" onSubmit={handleSubmit}>
            <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="font-semibold text-slate-800">Type aanvraag</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, requestType: "service" }))}
                  className={`rounded-lg px-3 py-1.5 font-semibold ${
                    form.requestType !== "offerte_aanvraag"
                      ? "bg-blue-700 text-white"
                      : "border border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  Service/storing melden
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({ ...current, requestType: "offerte_aanvraag" }))
                  }
                  className={`rounded-lg px-3 py-1.5 font-semibold ${
                    form.requestType === "offerte_aanvraag"
                      ? "bg-blue-700 text-white"
                      : "border border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  Offerte aanvragen
                </button>
              </div>
            </div>

            <label className="grid gap-1 text-sm text-slate-700">
              Waar gaat uw aanvraag over?
              <select
                value={form.requestType}
                onChange={(event) =>
                  setForm((current) => ({ ...current, requestType: event.target.value }))
                }
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="service">Service</option>
                <option value="storing">Storing</option>
                <option value="onderhoud">Onderhoud</option>
                <option value="inspectie">Inspectie</option>
                <option value="offerte_aanvraag">Offerte-aanvraag</option>
                <option value="overig">Overig</option>
              </select>
            </label>

            <label className="grid gap-1 text-sm text-slate-700">
              Onderwerp
              <input
                required
                value={form.subject}
                onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Bijv. storing brandmeldcentrale"
              />
            </label>

            {showContextSelectors ? (
              <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-900">Context (optioneel)</p>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="grid gap-1 text-sm text-slate-700">
                    Kies locatie
                    <select
                      value={form.quoteRequestMeta.locationId}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          quoteRequestMeta: {
                            ...current.quoteRequestMeta,
                            locationId: event.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                      disabled={requestOptionsLoading || requestOptions.locations.length === 0}
                    >
                      <option value="">Niet bekend / niet van toepassing</option>
                      {requestOptions.locations.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name || "Locatie"}{item.addressLabel ? ` - ${item.addressLabel}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-1 text-sm text-slate-700">
                    Kies installatie
                    <select
                      value={form.quoteRequestMeta.installationId}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          quoteRequestMeta: {
                            ...current.quoteRequestMeta,
                            installationId: event.target.value,
                            assetId: "",
                          },
                        }))
                      }
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                      disabled={requestOptionsLoading || filteredInstallations.length === 0}
                    >
                      <option value="">Niet bekend / niet van toepassing</option>
                      {filteredInstallations.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name || "Installatie"}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-1 text-sm text-slate-700">
                    Kies onderdeel/asset
                    <select
                      value={form.quoteRequestMeta.assetId}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          quoteRequestMeta: {
                            ...current.quoteRequestMeta,
                            assetId: event.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                      disabled={requestOptionsLoading || filteredAssets.length === 0}
                    >
                      <option value="">Niet bekend / niet van toepassing</option>
                      {filteredAssets.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name || "Asset"}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {!requestOptionsLoading && requestOptions.locations.length === 0 ? (
                  <p className="text-xs text-slate-600">Nog geen locaties/installaties gekoppeld.</p>
                ) : null}
                {!requestOptionsLoading && requestOptions.installations.length === 0 ? (
                  <p className="text-xs text-slate-600">Geen installaties gekoppeld aan uw klantportaal.</p>
                ) : null}
                {!requestOptionsLoading && requestOptions.assets.length === 0 ? (
                  <p className="text-xs text-slate-600">Nog geen assets gekoppeld aan uw klantportaal.</p>
                ) : null}
              </div>
            ) : null}

            <label className="grid gap-1 text-sm text-slate-700">
              Prioriteit
              <select
                value={form.prioriteit}
                onChange={(event) => setForm((current) => ({ ...current, prioriteit: event.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="laag">Laag</option>
                <option value="normaal">Normaal</option>
                <option value="hoog">Hoog</option>
                <option value="kritiek">Kritiek</option>
              </select>
            </label>

            <label className="grid gap-1 text-sm text-slate-700">
              Bericht
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Omschrijf je aanvraag zo duidelijk mogelijk."
              />
            </label>

            {form.requestType === "offerte_aanvraag" ? (
              <div className="grid gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">Offerte-aanvraag details</p>
                <label className="grid gap-1 text-sm text-amber-900">
                  Gewenste uitvoerdatum (optioneel)
                  <input
                    type="date"
                    value={form.quoteRequestMeta.desiredExecutionDate}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        quoteRequestMeta: {
                          ...current.quoteRequestMeta,
                          desiredExecutionDate: event.target.value,
                        },
                      }))
                    }
                    className="rounded-xl border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
                  />
                </label>
                <label className="grid gap-1 text-sm text-amber-900">
                  Budgetindicatie (optioneel)
                  <input
                    value={form.quoteRequestMeta.budgetIndication}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        quoteRequestMeta: {
                          ...current.quoteRequestMeta,
                          budgetIndication: event.target.value,
                        },
                      }))
                    }
                    placeholder="Bijv. circa € 2.500"
                    className="rounded-xl border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
                  />
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="grid gap-1 text-sm text-amber-900">
                    Contactpersoon op locatie (optioneel)
                    <input
                      value={form.quoteRequestMeta.contactPersonName}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          quoteRequestMeta: {
                            ...current.quoteRequestMeta,
                            contactPersonName: event.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
                    />
                  </label>
                  <label className="grid gap-1 text-sm text-amber-900">
                    Contact telefoon (optioneel)
                    <input
                      value={form.quoteRequestMeta.contactPersonPhone}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          quoteRequestMeta: {
                            ...current.quoteRequestMeta,
                            contactPersonPhone: event.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
                    />
                  </label>
                </div>
                <label className="grid gap-1 text-sm text-amber-900">
                  Contact e-mail (optioneel)
                  <input
                    type="email"
                    value={form.quoteRequestMeta.contactPersonEmail}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        quoteRequestMeta: {
                          ...current.quoteRequestMeta,
                          contactPersonEmail: event.target.value,
                        },
                      }))
                    }
                    className="rounded-xl border border-amber-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
                  />
                </label>
              </div>
            ) : null}

            <div>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
              >
                {saving ? "Versturen..." : "Aanvraag indienen"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">Mijn aanvragen</h2>
            <div className="flex flex-wrap gap-2">
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="Zoek op nummer of onderwerp"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="">Alle statussen</option>
                <option value="nieuw">Nieuw</option>
                <option value="open">Open</option>
                <option value="in_behandeling">In behandeling</option>
                <option value="wacht_op_klant">Wacht op klant</option>
                <option value="opgelost">Opgelost</option>
                <option value="gesloten">Gesloten</option>
              </select>
              <select
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="">Alle types</option>
                <option value="service">Service</option>
                <option value="storing">Storing</option>
                <option value="onderhoud">Onderhoud</option>
                <option value="inspectie">Inspectie</option>
                <option value="offerte_aanvraag">Offerte-aanvraag</option>
                <option value="overig">Overig</option>
              </select>
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between gap-2 px-1 text-xs text-slate-600">
            <p>
              Pagina <span className="font-semibold text-slate-800">{pagination.page}</span> van{" "}
              <span className="font-semibold text-slate-800">{pagination.totalPages}</span> • Totaal:{" "}
              <span className="font-semibold text-slate-800">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={!pagination.hasPrevious || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
                disabled={!pagination.hasNext || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <p className="py-4 text-sm text-slate-500">Laden...</p>
          ) : items.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">Nog geen aanvragen gevonden.</p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const id = String(item?.id || "");
                const selected = id === selectedId;
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => setSelectedId((current) => (current === id ? "" : id))}
                    className={`w-full rounded-xl border p-3 text-left ${
                      selected
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {item?.ticketNumber || "-"} • {item?.subject || "-"}
                        </p>
                        <p className="text-xs text-slate-600">
                          Type: {formatRequestType(item?.requestType)} • Aangemaakt: {formatDate(item?.createdAt)} • Prioriteit: {formatPriority(item?.prioriteit)}
                        </p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                        {formatStatus(item?.status)}
                      </span>
                    </div>

                    {selected ? (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700">
                        {detailLoading ? (
                          <p>Laden...</p>
                        ) : (
                          <>
                            <p className="font-semibold text-slate-900">Berichten</p>
                            {(detail?.messages || []).length === 0 ? (
                              <p className="mt-1">Nog geen berichten.</p>
                            ) : (
                              <div className="mt-2 space-y-2">
                                {detail.messages.map((message) => (
                                  <div key={message.id} className="rounded border border-slate-200 p-2">
                                    <p className="text-slate-900">{message.message}</p>
                                    <p className="mt-1 text-[11px] text-slate-500">
                                      {message.authorName || message.authorEmail || "Onbekend"} •{" "}
                                      {formatDateTime(message.createdAt)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function formatStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "-";
  if (value === "nieuw") return "Ontvangen";
  if (value === "open") return "Open";
  if (value === "in_behandeling") return "In behandeling";
  if (value === "wacht_op_klant") return "Actie nodig";
  if (value === "opgelost") return "Afgehandeld";
  if (value === "gesloten") return "Gesloten";
  return value.split("_").join(" ");
}

function formatPriority(priority) {
  const value = String(priority || "").trim().toLowerCase();
  if (!value) return "-";
  if (value === "laag") return "Laag";
  if (value === "normaal") return "Normaal";
  if (value === "hoog") return "Hoog";
  if (value === "kritiek") return "Kritiek";
  return value;
}

function formatRequestType(requestType) {
  const value = String(requestType || "").trim().toLowerCase();
  if (!value) return "Service";
  if (value === "offerte_aanvraag") return "Offerte-aanvraag";
  if (value === "storing") return "Storing";
  if (value === "onderhoud") return "Onderhoud";
  if (value === "inspectie") return "Inspectie";
  if (value === "overig") return "Overig";
  return "Service";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", { dateStyle: "medium" }).format(date);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
